/**
 * Règle unique d'état d'un employé.
 *
 * Auparavant, la liste des employés affichait un badge calculé sur la date de
 * fin de contrat pendant que la fiche affichait le champ `status` : un même
 * employé pouvait donc apparaître « INACTIF » dans la liste et « ACTIF » sur sa
 * fiche. Tout passe désormais par `getEmployeeState`.
 */

export interface EmployeeStatusLike {
  status?: string;
  contract_type?: string;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
}

/** Minuit aujourd'hui, pour comparer des dates sans effet d'heure. */
function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Le contrat est-il arrivé à échéance ?
 * Pas de date de fin (CDI) = jamais échu.
 */
export function isContractExpired(employee: EmployeeStatusLike): boolean {
  if (!employee.contract_end_date) return false;
  const end = new Date(employee.contract_end_date);
  end.setHours(0, 0, 0, 0);
  return end < today();
}

export interface EmployeeState {
  /** Libellé affiché dans le badge */
  label: string;
  /** Classe daisyUI du badge */
  badgeClass: string;
  /** L'employé est-il en activité ? */
  active: boolean;
}

/**
 * État affiché partout (liste, fiche, exports).
 *
 * La date de fin de contrat prime : un contrat échu rend l'employé inactif,
 * quel que soit le champ `status` resté à ACTIF faute de mise à jour.
 */
export function getEmployeeState(employee: EmployeeStatusLike): EmployeeState {
  if (isContractExpired(employee)) {
    return { label: 'Contrat terminé', badgeClass: 'badge-error', active: false };
  }

  switch (employee.status) {
    case 'CONGE':
      return { label: 'En congé', badgeClass: 'badge-warning', active: true };
    case 'SUSPENDU':
      return { label: 'Suspendu', badgeClass: 'badge-warning', active: false };
    case 'TERMINE':
      return { label: 'Contrat terminé', badgeClass: 'badge-error', active: false };
    case 'INACTIF':
      return { label: 'Inactif', badgeClass: 'badge-error', active: false };
    default:
      return { label: 'Actif', badgeClass: 'badge-success', active: true };
  }
}

// ---------------------------------------------------------------------------
// Plafond légal des CDD
// ---------------------------------------------------------------------------

/** Durée maximale d'un CDD, renouvellements compris (en mois). */
export const CDD_MAX_MONTHS = 24;

export interface CddDurationInfo {
  /** L'employé est-il en CDD ? */
  isCdd: boolean;
  /** Durée écoulée depuis le début du contrat, en mois entiers */
  months: number;
  /** Le plafond de 24 mois est-il atteint ou dépassé ? */
  limitReached: boolean;
  /** Le plafond sera-t-il atteint dans les 3 prochains mois ? */
  approaching: boolean;
  /** Date à laquelle le plafond de 24 mois est atteint (ISO) */
  limitDate: string | null;
}

/** Nombre de mois entiers écoulés entre deux dates. */
function monthsBetween(from: Date, to: Date): number {
  let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) months -= 1;
  return months;
}

/**
 * Analyse la durée d'un CDD au regard du plafond légal de 2 ans.
 *
 * La durée est comptée depuis la date de début de contrat : c'est elle qui
 * détermine si une prorogation ferait dépasser le plafond.
 */
export function getCddDuration(employee: EmployeeStatusLike): CddDurationInfo {
  const vide: CddDurationInfo = {
    isCdd: false,
    months: 0,
    limitReached: false,
    approaching: false,
    limitDate: null,
  };

  if ((employee.contract_type || '').toUpperCase() !== 'CDD') return vide;
  if (!employee.contract_start_date) return { ...vide, isCdd: true };

  const start = new Date(employee.contract_start_date);
  if (isNaN(start.getTime())) return { ...vide, isCdd: true };

  // Le plafond se juge sur la date de fin prévue si elle existe (une
  // prorogation se décide avant l'échéance), sinon sur aujourd'hui.
  const reference = employee.contract_end_date
    ? new Date(employee.contract_end_date)
    : today();

  const months = monthsBetween(start, reference);

  const limit = new Date(start);
  limit.setMonth(limit.getMonth() + CDD_MAX_MONTHS);

  return {
    isCdd: true,
    months,
    limitReached: months >= CDD_MAX_MONTHS,
    approaching: months >= CDD_MAX_MONTHS - 3 && months < CDD_MAX_MONTHS,
    limitDate: limit.toISOString().split('T')[0],
  };
}
